import React, { useEffect, useRef, useState } from 'react';
import { cn } from '../primitives';
import { MoodFace } from './MoodFace';
import { ActivityButton } from './ActivityButton';

// GraphQL response types
export interface Activity {
  id: string;
  active: boolean;
  value: number | null;
  field: {
    id: string;
    name: string;
    icon: string;
    score: number;
  };
}

export interface DiaryDay {
  id: string;
  date: string;
  mood: number | null;
  energy: number | null;
  note: string | null;
  activities: Activity[];
}

// Filter option for activity filtering
export interface FilterOption {
  id: string;
  name: string;
  icon: string;
  score: number;
  widgetName: string;
  fieldSetName: string | null;
}

export interface AgendaPageProps {
  diaryDays: DiaryDay[];
  page: number;
  hasMore: boolean;
  onLoadMore: () => void;
  onDayClick: (date: string) => void;
  isLoading?: boolean;
  filteredFieldIds?: string[];
  availableFields?: FilterOption[];
  onFilterChange?: (fieldIds: string[]) => void;
  onMonthChange?: (direction: 'prev' | 'next') => void;
  currentMonth?: string;
  formatDate?: (date: string) => string;
  iconBaseUrl?: string;
}


function AgendaDayCard({ diaryDay, onDayClick, filteredFieldIds = [], iconBaseUrl = '/assets' }: {
  diaryDay: DiaryDay;
  onDayClick: (date: string) => void;
  filteredFieldIds?: string[];
  iconBaseUrl?: string;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  };

  const getScoreColorClass = (mood: number | null) => {
    if (mood === null) return '';
    if (mood >= 60) return 'bg-success bg-opacity-50';
    if (mood >= 40) return 'bg-warning bg-opacity-50';
    return 'bg-danger bg-opacity-50';
  };

  const activeActivities = diaryDay.activities.filter(a => a.active);
  const hasActiveFilter = filteredFieldIds.length > 0;

  // Sort activities to show filtered ones first
  const sortedActivities = hasActiveFilter
    ? [...activeActivities].sort((a, b) => {
        const aFiltered = filteredFieldIds.includes(a.field.id);
        const bFiltered = filteredFieldIds.includes(b.field.id);
        if (aFiltered && !bFiltered) return -1;
        if (!aFiltered && bFiltered) return 1;
        return 0;
      })
    : activeActivities;

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="col-12 mb-4">
      <div
        className="card agenda-item"
        onClick={() => onDayClick(diaryDay.date)}
        style={{ cursor: 'pointer' }}
      >
        <div className={cn("card-header d-flex justify-content-between align-items-center", getScoreColorClass(diaryDay.mood))}>
          <div className="agenda-item-mood float-start">
            <MoodFace mood={diaryDay.mood} />
          </div>
          <h5 className="mb-0 float-end">{formatDate(diaryDay.date)}</h5>
        </div>
        <div className="card-body">
          {activeActivities.length > 0 && (
            <>
              {/* Compact view - using ActivityButton */}
              {!isExpanded && (
                <div className="d-flex flex-wrap gap-0 mb-0 align-items-start agenda-activities-compact">
                  {sortedActivities.map(activity => {
                    const isHighlighted = hasActiveFilter && filteredFieldIds.includes(activity.field.id);

                    return (
                      <div
                        key={activity.id}
                        className={cn(
                          'agenda-activity-wrapper',
                          isHighlighted && 'agenda-activity-wrapper--highlighted'
                        )}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          transform: 'scale(0.45)',
                          transformOrigin: 'top left',
                          marginRight: '-39px',
                          marginBottom: '-50px',
                          opacity: hasActiveFilter && !isHighlighted ? 0.4 : 1,
                        }}
                      >
                        <ActivityButton
                          id={activity.field.id}
                          name=""
                          icon={activity.field.icon}
                          variant="toggle"
                          active={true}
                          score={activity.field.score}
                        />
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Expanded view - icons with names */}
              {isExpanded && (
                <div className="agenda-activities-expanded mb-2">
                  <div className="d-flex flex-wrap gap-1 align-items-start">
                    {sortedActivities.map(activity => {
                      const isHighlighted = hasActiveFilter && filteredFieldIds.includes(activity.field.id);

                      return (
                        <div
                          key={activity.id}
                          className={cn(
                            'agenda-activity-wrapper',
                            isHighlighted && 'agenda-activity-wrapper--highlighted'
                          )}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            transform: 'scale(0.8)',
                            transformOrigin: 'top left',
                            marginRight: '-14px',
                            marginBottom: '-18px',
                            opacity: hasActiveFilter && !isHighlighted ? 0.4 : 1,
                          }}
                        >
                          <ActivityButton
                            id={activity.field.id}
                            name={activity.field.name}
                            icon={activity.field.icon}
                            variant="toggle"
                            active={true}
                            score={activity.field.score}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Expand/Collapse button */}
              <button
                className="btn btn-sm btn-link text-muted p-0 d-flex align-items-center gap-1"
                onClick={handleExpandClick}
                style={{ fontSize: '12px' }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                  }}
                >
                  <polyline points="6,9 12,15 18,9" />
                </svg>
                {isExpanded ? 'Show less' : `Show ${activeActivities.length} ${activeActivities.length === 1 ? 'activity' : 'activities'}`}
              </button>
            </>
          )}
          {diaryDay.note && (
            <div className="card-text mt-2">
              <hr />
              <span className="note-preview">
                {diaryDay.note.length > 100
                  ? `${diaryDay.note.substring(0, 100)}...`
                  : diaryDay.note
                }
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Group structure for organizing fields
interface FieldGroup {
  widgetName: string;
  directFields: FilterOption[];
  fieldSets: Map<string, FilterOption[]>;
}

// Activity Filter Component
function ActivityFilter({
  availableFields,
  selectedFieldIds,
  onFilterChange,
  isLoading,
}: {
  availableFields: FilterOption[];
  selectedFieldIds: string[];
  onFilterChange: (fieldIds: string[]) => void;
  isLoading?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const toggleField = (fieldId: string) => {
    if (selectedFieldIds.includes(fieldId)) {
      onFilterChange(selectedFieldIds.filter(id => id !== fieldId));
    } else {
      onFilterChange([...selectedFieldIds, fieldId]);
    }
  };

  const clearFilters = () => {
    onFilterChange([]);
  };

  // Filter fields by search query
  const filteredFields = React.useMemo(() => {
    if (!searchQuery.trim()) return availableFields;
    const query = searchQuery.toLowerCase();
    return availableFields.filter(field =>
      field.name.toLowerCase().includes(query) ||
      field.widgetName.toLowerCase().includes(query) ||
      (field.fieldSetName && field.fieldSetName.toLowerCase().includes(query))
    );
  }, [availableFields, searchQuery]);

  // Group fields by widget and fieldset
  const groupedFields = React.useMemo(() => {
    const groups = new Map<string, FieldGroup>();

    for (const field of filteredFields) {
      if (!groups.has(field.widgetName)) {
        groups.set(field.widgetName, {
          widgetName: field.widgetName,
          directFields: [],
          fieldSets: new Map(),
        });
      }

      const group = groups.get(field.widgetName)!;

      if (field.fieldSetName) {
        if (!group.fieldSets.has(field.fieldSetName)) {
          group.fieldSets.set(field.fieldSetName, []);
        }
        group.fieldSets.get(field.fieldSetName)!.push(field);
      } else {
        group.directFields.push(field);
      }
    }

    return Array.from(groups.values());
  }, [filteredFields]);

  const renderFieldButton = (field: FilterOption) => {
    const isSelected = selectedFieldIds.includes(field.id);

    return (
      <div
        key={field.id}
        className={cn(
          'agenda-filter-item',
          isSelected && 'agenda-filter-item--selected'
        )}
        style={{
          transform: 'scale(0.75)',
          transformOrigin: 'top left',
          marginRight: '-18px',
          marginBottom: '-22px',
          opacity: isSelected ? 1 : 0.6,
        }}
      >
        <ActivityButton
          id={field.id}
          name={field.name}
          icon={field.icon}
          variant="toggle"
          active={isSelected}
          score={field.score}
          onClick={() => toggleField(field.id)}
        />
      </div>
    );
  };

  return (
    <div className="agenda-filter mb-4">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <button
          className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
          onClick={() => setIsExpanded(!isExpanded)}
          disabled={isLoading}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46" />
          </svg>
          Filter by Activities
          {selectedFieldIds.length > 0 && (
            <span className="badge bg-primary">{selectedFieldIds.length}</span>
          )}
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            style={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          >
            <polyline points="6,9 12,15 18,9" />
          </svg>
        </button>
        {selectedFieldIds.length > 0 && (
          <button
            className="btn btn-link btn-sm text-danger p-0"
            onClick={clearFilters}
          >
            Clear filters
          </button>
        )}
      </div>

      {isExpanded && (
        <div className="agenda-filter-options p-3 border rounded bg-light">
          {availableFields.length === 0 ? (
            <p className="text-muted mb-0">No activities available for filtering</p>
          ) : (
            <div>
              {/* Search input */}
              <div className="mb-3">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search activities..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {groupedFields.length === 0 && searchQuery && (
                <p className="text-muted mb-0">No activities match "{searchQuery}"</p>
              )}

              {groupedFields.map((group, groupIndex) => (
                <div key={group.widgetName} className={groupIndex > 0 ? 'mt-3' : ''}>
                  <h6 className="text-muted mb-2 fw-semibold" style={{ fontSize: '0.85rem' }}>
                    {group.widgetName}
                  </h6>

                  {/* Direct fields (not in a fieldset) */}
                  {group.directFields.length > 0 && (
                    <div className="d-flex flex-wrap gap-1 align-items-start mb-2">
                      {group.directFields.map(renderFieldButton)}
                    </div>
                  )}

                  {/* Fieldsets */}
                  {Array.from(group.fieldSets.entries()).map(([fieldSetName, fields]) => (
                    <div key={fieldSetName} className="ms-2 mb-2">
                      <small className="text-muted d-block mb-1" style={{ fontSize: '0.75rem' }}>
                        {fieldSetName}
                      </small>
                      <div className="d-flex flex-wrap gap-1 align-items-start">
                        {fields.map(renderFieldButton)}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function AgendaPage({
  diaryDays,
  page,
  hasMore,
  onLoadMore,
  onDayClick,
  isLoading = false,
  filteredFieldIds = [],
  availableFields = [],
  onFilterChange,
  iconBaseUrl = '/assets',
}: AgendaPageProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [monthGroups, setMonthGroups] = useState<Map<string, DiaryDay[]>>(new Map());

  // Group diary days by month
  useEffect(() => {
    const groups = new Map<string, DiaryDay[]>();
    diaryDays.forEach(day => {
      const date = new Date(day.date);
      const monthKey = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });

      if (!groups.has(monthKey)) {
        groups.set(monthKey, []);
      }
      groups.get(monthKey)!.push(day);
    });
    setMonthGroups(groups);
  }, [diaryDays]);

  // Infinite scroll observer
  useEffect(() => {
    if (!sentinelRef.current || !hasMore || isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, [hasMore, isLoading, onLoadMore]);

  return (
    <div className="container">
      {/* Activity Filter */}
      {onFilterChange && (
        <ActivityFilter
          availableFields={availableFields}
          selectedFieldIds={filteredFieldIds}
          onFilterChange={onFilterChange}
          isLoading={isLoading}
        />
      )}

      {/* Filter Active Indicator */}
      {filteredFieldIds.length > 0 && (
        <div className="alert alert-info py-2 mb-3 d-flex align-items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>
            Showing days with {filteredFieldIds.length} selected {filteredFieldIds.length === 1 ? 'activity' : 'activities'}.
            Filtered activities are shown first.
          </span>
        </div>
      )}

      <div className="row">
        {Array.from(monthGroups.entries()).map(([monthKey, days]) => (
          <div key={monthKey} className="col-12">
            <h3 className="month-header mb-3">{monthKey}</h3>
            {days.map(day => (
              <AgendaDayCard
                key={day.id}
                diaryDay={day}
                onDayClick={onDayClick}
                filteredFieldIds={filteredFieldIds}
                iconBaseUrl={iconBaseUrl}
              />
            ))}
          </div>
        ))}

        {hasMore && (
          <div ref={sentinelRef} className="col-12 text-center py-4">
            {isLoading ? (
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            ) : (
              <p className="text-muted">Scroll to load more...</p>
            )}
          </div>
        )}

        {!hasMore && diaryDays.length > 0 && (
          <div className="col-12 text-center py-4">
            <p className="text-muted">No more entries</p>
          </div>
        )}

        {diaryDays.length === 0 && !isLoading && (
          <div className="col-12 text-center py-4">
            <p className="text-muted">No diary entries found</p>
          </div>
        )}
      </div>
    </div>
  );
}
